import React, { ChangeEventHandler, useId, useMemo, ReactNode } from "react";
import {
  Input,
  InputGroup,
  NumberInput,
  NumberInputRootProps,
} from "@chakra-ui/react";
import "./CustomTextInput.scss";
import { objectToClassString } from "../../utils/functions";
import classNames from "classnames";

interface ICustomTextInput<T> {
  label?: string;
  value: string | number | undefined;
  className?: string;
  classNameInput?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  type?: T;
  onChange?: T extends "number"
    ? (valueAsString: string, valueAsNumber: number) => void
    : ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  rightAddon?: ReactNode;
  onClickRightAddon?: () => void;
  onValueNumberChange?: NumberInputRootProps["onValueChange"];
}

const CustomTextInput = <
  T extends "text" | "tel" | "date" | "email" | "number" | "password" = "text"
>({
  label,
  onChange,
  value = "",
  className,
  classNameInput,
  min,
  max,
  disabled = false,
  type: typeInput,
  placeholder,
  rightAddon,
  onClickRightAddon,
  onValueNumberChange,
}: ICustomTextInput<T>) => {
  // const { size } = useWindowDimensions();
  const type = typeInput ?? "text";
  // BUG-A11Y-008 fix: associate label to input via htmlFor/id (WCAG 1.3.1).
  // Era `<span class="label">` con nessuna associazione semantica.
  const inputId = useId();

  const inputMemo = useMemo(
    () => (
      <div className={`container__custom__input ${className}`}>
        {label && (
          <label htmlFor={inputId} className="label">
            {label}
          </label>
        )}
        <InputGroup endElement={rightAddon ? rightAddon : undefined}>
          <>
            {type === "number" ? (
              <NumberInput.Root
                min={min}
                max={max}
                className={classNames(classNameInput)}
                value={isNaN(+value) ? undefined : String(value)}
                onValueChange={onValueNumberChange}
                disabled={disabled}
              >
                <NumberInput.Control />
                <NumberInput.Input
                  id={inputId}
                  placeholder={placeholder}
                  className={`custom__input ${objectToClassString({
                    with__addon: Boolean(rightAddon),
                  })}`}
                />
              </NumberInput.Root>
            ) : (
              <Input
                id={inputId}
                type={type}
                min={min}
                max={max}
                value={value}
                onChange={onChange as ChangeEventHandler<HTMLInputElement>}
                disabled={disabled}
                placeholder={placeholder}
                className={`custom__input ${objectToClassString({
                  with__addon: Boolean(rightAddon),
                })}`}
              />
            )}
          </>
        </InputGroup>
      </div>
    ),
    // eslint-disable-next-line
    [value, className, label, disabled, rightAddon, inputId]
  );
  return <>{inputMemo}</>;
};

export default CustomTextInput;
